import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';

import { CustomChannelType, isChannelSilenced } from 'app/types/chat';
import {
  useArchiveChannel,
  useBatchUnreadChannels,
  useUnArchiveChannel,
  useUpdateChannelSilenced,
} from 'app/network/chat/channel';
import { useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, View } from 'react-native';
import Swipeable from 'react-native-gesture-handler/ReanimatedSwipeable';
import { useSwipeableReset } from 'app/hooks/use-swipeable-reset';
import { ListingChannel } from './listing-channel';
import { MemberChannel } from './member-channel';
import { SupportChannel } from './support-channel';
import type { Channel } from 'app/types/chat';
import { Checkbox } from 'app/design/primitives/checkbox';
import { useRoute } from 'app/hooks/navigation';
import { useUISize } from 'app/utils/web-helpers';
import { GroupChannel } from './group-channel';
import { Unread } from 'app/design/icons/unread';
import { SilenceConversationBottomSheet } from 'app/screens/chat/components/silence-conversation-bottom-sheet';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconArchive, IconBell, IconBellOff } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

interface GroupChannelProps {
  channel: Channel & { isSelected?: boolean };
  enableSwipeActions: boolean;
  onArchive: () => void;
  onUnarchive: () => void;
  onPress: (channelUrl: string, unread_message_count: number) => void;
  userId?: string;
  listingId?: string;
  isSelecting?: boolean;
  setSelectedChannels?: Dispatch<SetStateAction<string[]>>;
  isFirstConcierge?: boolean;
  archived?: boolean;
  search?: string;
}

const useLocalArchivedState = (channelUrl: string, initialArchived: boolean | undefined) => {
  const source = `${channelUrl}:${String(initialArchived ?? false)}`;
  const [state, setState] = useState({ source, value: initialArchived ?? false });
  let currentState = state;
  if (state.source !== source) {
    currentState = { source, value: initialArchived ?? false };
    setState(currentState);
  }
  return {
    archived: currentState.value,
    setArchived: (value: boolean) => setState({ source, value }),
    setArchivedState: setState,
  };
};

const ChannelPreview = ({
  channel,
  isFirstConcierge,
  listingId,
}: {
  channel: Channel;
  isFirstConcierge: boolean | undefined;
  listingId: string | undefined;
}) => {
  switch (channel.custom_type) {
    case CustomChannelType.Listing:
      return (
        <ListingChannel {...channel} isFirstConcierge={isFirstConcierge} useInterlocutorPicture={Boolean(listingId)} />
      );
    case CustomChannelType.Member:
      return <MemberChannel {...channel} />;
    case CustomChannelType.Group:
      return <GroupChannel {...channel} />;
    case CustomChannelType.Support:
      return <SupportChannel {...channel} />;
    default:
      return null;
  }
};

const ChannelListItem = function ChannelListItem({
  channel,
  onArchive,
  onPress,
  onUnarchive,
  isSelecting,
  setSelectedChannels,
  enableSwipeActions,
  isFirstConcierge,
  userId,
  listingId,
  search,
  archived: _archived, // archived is only used to determine the initial state of the item when swiping, after that we rely on the local archived state to avoid conflicts with the channel list refreshing while swiping
}: GroupChannelProps) {
  const { archived, setArchived, setArchivedState } = useLocalArchivedState(channel.channel_url, _archived);
  const archivedSource = `${channel.channel_url}:${String(_archived ?? false)}`;
  const [silenceBottomSheetOpen, setSilenceBottomSheetOpen] = useState(false);
  const route = useRoute<'one-channel'>();
  const currentRoute = useRoute().name;
  const openedChannelUrl = currentRoute === 'one-channel' ? route.params.url : undefined;
  const { isLargeUI } = useUISize();

  const swipeable = useSwipeableReset([channel.channel_url]);

  const archiveChannel = useArchiveChannel({
    listingId,
    userId,
    invalidateRecents: true,
    invalidateRecentsDelayMs: 350,
  });

  const { mutate: markChannelsAsUnread } = useBatchUnreadChannels({ search, archived: _archived, listingId, userId });

  const { mutate: updateSilenced } = useUpdateChannelSilenced({ search, archived: _archived, listingId, userId });
  const channelSilenced = isChannelSilenced(channel);

  const doArchiveAction = () => {
    setArchived(true);
    swipeable.current?.close();
    archiveChannel.mutate(channel.channel_url);
    onArchive();
  };

  const doMarkAsUnread = () => {
    swipeable.current?.close();
    markChannelsAsUnread([channel.channel_url]);
  };

  const doSilence = () => {
    swipeable.current?.close();
    if (channelSilenced) {
      updateSilenced({ url: channel.channel_url, silenced: false });
      return;
    }
    setSilenceBottomSheetOpen(true);
  };

  const onSelectSilenceDuration = (durationMinutes?: number) => {
    updateSilenced({ url: channel.channel_url, silenced: true, durationMinutes });
  };

  const unarchiveChannel = useUnArchiveChannel({ invalidateArchived: true, listingId, userId });

  const undoArchive = () => {
    setArchived(false);
    unarchiveChannel.mutate(channel.channel_url);
    swipeable.current?.close();
    onUnarchive();
  };

  useEffect(() => {
    if (archived || channel.unread_message_count <= 0) {
      return;
    }

    const timer = setTimeout(() => {
      setArchivedState({ source: archivedSource, value: false });
      swipeable.current?.close();
    }, 3000);

    return () => clearTimeout(timer);
  }, [archived, archivedSource, channel.unread_message_count, setArchivedState, swipeable]);

  const renderRightActions = () => (
    <Row
      style={{ alignSelf: 'stretch', marginLeft: theme.spacing['spacing.general.x4'] }}
      alignItems="center"
      gap="spacing.general.x1"
    >
      {archived ? (
        <UnarchiveButton onPress={undoArchive} />
      ) : (
        <>
          <SilenceButton silenced={channelSilenced} onPress={doSilence} />
          {channel.unread_message_count === 0 ? <UnreadButton onPress={doMarkAsUnread} /> : null}
          <ArchiveButton onPress={doArchiveAction} />
        </>
      )}
    </Row>
  );

  const onSelectRow = () => {
    if (setSelectedChannels === undefined) return;
    setSelectedChannels((prev) => {
      if (!channel.isSelected) {
        return [...prev, channel.channel_url];
      }
      return prev.filter((c) => c !== channel.channel_url);
    });
  };

  const onPressOrSelect = (channelUrl: string, unread_message_count: number) => {
    if (isSelecting) {
      onSelectRow();
      return;
    }
    onPress(channelUrl, unread_message_count);
  };

  const addBackgroundColorOnCurrentChannel = openedChannelUrl === channel.channel_url && isLargeUI;

  return (
    <View style={{ width: '100%' }}>
      <Swipeable
        enabled={enableSwipeActions}
        friction={2}
        ref={swipeable}
        renderRightActions={renderRightActions}
        rightThreshold={80}
        containerStyle={{ width: '100%', alignSelf: 'stretch' }}
      >
        <Pressable
          accessibilityLabel={channel.name}
          accessibilityRole={isSelecting ? 'checkbox' : 'button'}
          accessibilityState={isSelecting ? { checked: Boolean(channel.isSelected) } : undefined}
          onPress={() => onPressOrSelect(channel.channel_url, channel.unread_message_count)}
        >
          <Row
            collapsable={false}
            style={{
              backgroundColor: addBackgroundColorOnCurrentChannel
                ? theme.colors['surface.inverse.tertiary-neutral']
                : theme.colors['surface.default'],
              alignItems: 'center',
              alignSelf: 'stretch',
              gap: theme.spacing['spacing.general.x4'],
              borderRadius: isLargeUI ? theme.borderRadii['radius.lg'] : 0,
              paddingHorizontal: addBackgroundColorOnCurrentChannel ? theme.spacing['spacing.general.x1'] : 0,
              paddingVertical: addBackgroundColorOnCurrentChannel ? theme.spacing['spacing.general.x1'] : 0,
              cursor: 'pointer',
              width: '100%',
            }}
          >
            {isSelecting ? <Checkbox checked={channel.isSelected} /> : null}
            <ChannelPreview {...{ channel, isFirstConcierge, listingId }} />
          </Row>
        </Pressable>
      </Swipeable>
      <SilenceConversationBottomSheet
        open={silenceBottomSheetOpen}
        onClose={() => setSilenceBottomSheetOpen(false)}
        onSelectDuration={onSelectSilenceDuration}
      />
    </View>
  );
};

export { ChannelListItem };

const ArchiveButton = function ArchiveButton({ onPress }: { onPress: () => void }) {
  const { t } = useTranslation('all-channels');
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={t('archive')}
      style={{ alignSelf: 'stretch' }}
    >
      <Column
        alignItems="center"
        justifyContent="center"
        style={{
          alignSelf: 'stretch',
          height: '100%',
          backgroundColor: theme.colors['surface.tertiary'],
          paddingHorizontal: theme.spacing['spacing.general.x2'],
          paddingVertical: theme.spacing['spacing.general.x1'],
          borderTopRightRadius: theme.borderRadii['radius.sm'],
          borderBottomRightRadius: theme.borderRadii['radius.sm'],
          minWidth: 72,
        }}
      >
        <View style={{ marginTop: 4 }}>
          <IconArchive strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.primary']} />
        </View>
        <Text color="text.primary" style={{ fontSize: 12 }} variant="bodyBase">
          {t('archive')}
        </Text>
      </Column>
    </Pressable>
  );
};

const UnarchiveButton = function UnarchiveButton({ onPress }: { onPress: () => void }) {
  const { t } = useTranslation('all-channels');
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={t('archivedUndo')}
      style={{ alignSelf: 'stretch' }}
    >
      <Column
        alignItems="center"
        justifyContent="center"
        style={{
          alignSelf: 'stretch',
          height: '100%',
          backgroundColor: theme.colors['surface.tertiary'],
          paddingHorizontal: theme.spacing['spacing.general.x2'],
          paddingVertical: theme.spacing['spacing.general.x1'],
          borderRadius: theme.borderRadii['radius.sm'],
          minWidth: 72,
        }}
      >
        <View style={{ marginTop: 4 }}>
          <IconArchive strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.primary']} />
        </View>
        <Text color="text.primary" style={{ fontSize: 12 }} variant="bodyBase">
          {t('archivedUndo')}
        </Text>
      </Column>
    </Pressable>
  );
};

const UnreadButton = function UnreadButton({ onPress }: { onPress: () => void }) {
  const { t } = useTranslation('all-channels');
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={t('markAsUnreadSwipe')}
      style={{ alignSelf: 'stretch' }}
    >
      <Column
        alignItems="center"
        justifyContent="center"
        style={{
          alignSelf: 'stretch',
          height: '100%',
          backgroundColor: theme.colors['surface.brand.secondary-4.subtle'],
          paddingHorizontal: theme.spacing['spacing.general.x2'],
          paddingVertical: theme.spacing['spacing.general.x1'],
          minWidth: 72,
        }}
      >
        <View style={{ marginTop: 4 }}>
          <Unread color={theme.colors['text.primary']} />
        </View>
        <Text color="text.primary" style={{ fontSize: 12 }} variant="bodyBase">
          {t('markAsUnreadSwipe')}
        </Text>
      </Column>
    </Pressable>
  );
};

const SilenceButton = function SilenceButton({ silenced, onPress }: { silenced?: boolean; onPress: () => void }) {
  const { t } = useTranslation('all-channels');
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={t(silenced ? 'unsilence' : 'silence')}
      style={{ alignSelf: 'stretch' }}
    >
      <Column
        alignItems="center"
        justifyContent="center"
        style={{
          alignSelf: 'stretch',
          height: '100%',
          backgroundColor: theme.colors['surface.brand.secondary-5.subtle'],
          paddingHorizontal: theme.spacing['spacing.general.x2'],
          paddingVertical: theme.spacing['spacing.general.x1'],
          borderTopLeftRadius: theme.borderRadii['radius.sm'],
          borderBottomLeftRadius: theme.borderRadii['radius.sm'],
          minWidth: 72,
        }}
      >
        <View style={{ marginTop: 4 }}>
          {!silenced ? (
            <IconBellOff strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.primary']} />
          ) : (
            <IconBell strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.primary']} />
          )}
        </View>
        <Text color="text.primary" style={{ fontSize: 12 }} variant="bodyBase">
          {t(silenced ? 'unsilence' : 'silence')}
        </Text>
      </Column>
    </Pressable>
  );
};
